/**
 * What the signed-in role may do, per feature.
 *
 * The grants come from `GET /users/me`, which reports them from `os_role_permission_feature` — the same
 * rows the platform enforces with. These helpers are pure and take the grants as an argument, because
 * `core/` may not reach into `features/`; `features/authentication` wraps them in a hook that supplies them.
 *
 * **This is not a security boundary.** The platform refuses a call its grants do not allow, and will do
 * so whatever the client believes. What this decides is what to *show*, so a user is not offered a
 * button that answers 403.
 */

/** The five actions the RBAC matrix scores every feature against. `view` is the one that shows a feature. */
export const FEATURE_ACTIONS = ['view', 'create', 'read', 'update', 'delete'] as const;

export type FeatureAction = (typeof FEATURE_ACTIONS)[number];

/** One feature and the permissions the role holds on it, as `/users/me` reports them. */
export interface FeatureGrant {
  feature: string;
  permissions: readonly string[];
}

/** Feature names are compared case- and whitespace-insensitively; they are display names, not keys. */
function normalise(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Whether the grants hold one action on one feature.
 *
 * Strict: an absent feature and an absent permission both answer `false`. Deciding what an *empty* grant
 * list means is policy, and belongs to the caller — see `useFeatureAccess`.
 */
export function canOnFeature(
  grants: readonly FeatureGrant[] | undefined,
  feature: string,
  action: FeatureAction,
): boolean {
  if (!grants || grants.length === 0) return false;

  const wanted = normalise(feature);
  const entry = grants.find((grant) => normalise(grant.feature) === wanted);
  if (!entry) return false;

  return entry.permissions.some((permission) => normalise(permission) === action);
}

/**
 * Whether the role may reach a feature at all — the test a menu entry or a route asks.
 *
 * VIEW, not READ: every new role holds READ on every item by default, so READ cannot be what puts a menu
 * in front of a role. VIEW is ticked on purpose in the RBAC screen, and CREATE, UPDATE or DELETE carry it
 * with them.
 */
export function canReachFeature(
  grants: readonly FeatureGrant[] | undefined,
  feature: string,
): boolean {
  return canOnFeature(grants, feature, 'view');
}
